import { createI18n } from 'vue-i18n'
import { isStoryblokEnabled } from '@/constants/storyblok.js'

const SUPPORTED_LOCALES = ['en', 'nl', 'de', 'fr', 'es', 'it', 'pl', 'cs', 'da']

/**
 * Every bundled locale file in ./locales, keyed by its locale code
 * (e.g. { en: {...}, nl: {...} }). Drop a new `<code>.json` in that
 * folder and it is picked up automatically — no edits needed here or
 * in the Storyblok service.
 */
const localeModules = import.meta.glob('./locales/*.json', { eager: true })
export const bundledMessages = Object.fromEntries(
    Object.entries(localeModules).map(([path, mod]) => {
        const code = path.match(/\/([^/]+)\.json$/)[1]
        return [code, mod.default]
    }),
)

/**
 * Detect the preferred locale using, in order of priority:
 *  1. `document.lang`, set directly by the native app's JS bridge (not the
 *     <html lang="..."> attribute, which the webview no longer populates)
 *  2. The browser / WebView navigator.language
 *  3. English as final fallback
 *
 * Always English when Storyblok is disabled: the translations are managed
 * there, so without it they aren't signed off for this environment.
 */
export function detectLocale() {
    if (!isStoryblokEnabled) return 'en'

    const candidates = [
        document.lang,
        navigator.language,
    ]

    for (const raw of candidates) {
        if (!raw) continue
        // Accept exact match (e.g. 'nl') or prefix match (e.g. 'nl-NL' → 'nl')
        const code = raw.toLowerCase().split(/[-_]/)[0]
        if (SUPPORTED_LOCALES.includes(code)) return code
    }

    return 'en'
}

export function createI18nInstance(initialLocale = 'en') {
    return createI18n({
        legacy: false,
        locale: initialLocale,
        fallbackLocale: 'en',
        messages: bundledMessages,
        missingWarn: false,
        fallbackWarn: false,
    })
}
